import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2, type LucideIcon } from 'lucide-react';
import { cx } from './cx';

/**
 * Bouton de la charte.
 * - `primary` : dégradé indigo, UN SEUL par écran (l'action principale).
 * - `secondary` : pilule blanche = action courante.
 * - `soft` : fond indigo pâle, action mise en avant sans concurrencer le principal.
 * - `ghost` : discret, pour les barres d'outils.
 * - `link` : lien d'action dans un texte.
 * - `light` : bouton blanc sur fond indigo (encarts colorés).
 */
export type ButtonVariant = 'primary' | 'secondary' | 'soft' | 'ghost' | 'link' | 'light';
export type ButtonSize = 'md' | 'sm';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Icône Lucide affichée avant le libellé. */
  icon?: LucideIcon;
  /** Icône Lucide affichée après le libellé (ex. chevron de menu). */
  iconEnd?: LucideIcon;
  /** Bouton carré sans libellé visible : `aria-label` obligatoire. */
  iconOnly?: boolean;
  loading?: boolean;
  children?: ReactNode;
}

const base =
  'relative inline-flex items-center justify-center whitespace-nowrap rounded-pill font-medium transition-all duration-200 ease-out ' +
  'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-100 ' +
  'disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none disabled:translate-y-0';

const variants: Record<ButtonVariant, string> = {
  primary:
    'overflow-hidden bg-btn-primary font-semibold text-white shadow-btn ' +
    "before:pointer-events-none before:absolute before:inset-0 before:bg-btn-shine before:opacity-60 before:content-[''] " +
    'enabled:hover:-translate-y-0.5 enabled:hover:shadow-btn-hover',
  secondary:
    'border border-line bg-white text-body [&_svg]:text-primary ' +
    'enabled:hover:-translate-y-px enabled:hover:border-primary-200 enabled:hover:shadow-navbar',
  soft: 'bg-primary-50 text-primary-600 enabled:hover:bg-primary-100',
  ghost: 'text-secondary enabled:hover:bg-surface-2 enabled:hover:text-body',
  link: '!h-auto !px-0 font-medium text-primary-600 enabled:hover:text-primary-700 enabled:hover:underline underline-offset-[3px]',
  light:
    'bg-btn-light font-semibold text-primary-700 shadow-btn-light ' +
    'enabled:hover:-translate-y-0.5 enabled:hover:shadow-btn-light-hover',
};

const sizes: Record<ButtonSize, { box: string; square: string; icon: number }> = {
  md: { box: 'h-10 gap-2 px-[18px] text-sm', square: 'h-10 w-10', icon: 16 },
  sm: { box: 'h-8 gap-1.5 px-[13px] text-[13px]', square: 'h-8 w-8', icon: 15 },
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    icon: Icon,
    iconEnd: IconEnd,
    iconOnly = false,
    loading = false,
    type = 'button',
    className,
    disabled,
    children,
    ...props
  },
  ref,
) {
  const s = sizes[size];
  const LeadIcon = loading ? Loader2 : Icon;
  return (
    <button
      ref={ref}
      type={type}
      className={cx(base, variants[variant], iconOnly ? cx(s.square, 'p-0') : s.box, className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {LeadIcon && (
        <LeadIcon
          size={s.icon}
          strokeWidth={1.9}
          className={cx('relative shrink-0', loading && 'animate-spin')}
          aria-hidden
        />
      )}
      {!iconOnly && children != null && <span className="relative">{children}</span>}
      {IconEnd && !iconOnly && (
        <IconEnd size={14} strokeWidth={1.9} className="relative -mr-1 shrink-0 opacity-60" aria-hidden />
      )}
    </button>
  );
});
